import React, { useEffect, useMemo, useState } from "react";
import {
  LayoutDashboard, Users2, Scale, BadgeCheck, TrendingUp,
  MapPin, Star, Check, X, Clock4, Crown, ArrowUpRight, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { auth, api } from "../../lib/api";

const LEAD_STATUS = {
  "New": "bg-[#EAF6D6] text-[#466B1F]",
  "Assigned": "bg-[#D5EAE6] text-[#0B5447]",
  "Quote sent": "bg-[#D5EAE6] text-[#0B5447]",
  "Scheduled": "bg-amber-100 text-amber-700",
  "Filing": "bg-[#CDEFD4] text-[#1B6B34]",
};

const ADMIN_AVATAR = "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=200&q=85";

export default function AdminDashboard() {
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const admin = auth.user || { name: "Admin", role: "admin" };

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l, a, ap] = await Promise.all([
        api.adminStats(), api.adminLeads(), api.adminAttorneys(), api.adminApprovals(),
      ]);
      setStats(s); setLeads(l); setAttorneys(a); setApprovals(ap);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const nav = [
    { id: "overview", label: "Overview", desc: "Platform metrics & activity", icon: LayoutDashboard },
    { id: "leads", label: "Leads", desc: "Customer briefs · assign to attorneys", icon: Users2, badge: leads.length || undefined },
    { id: "attorneys", label: "Attorneys", desc: "All onboarded firms", icon: Scale },
    { id: "approvals", label: "Approvals", desc: "Pending verification queue", icon: BadgeCheck, badge: approvals.length || undefined },
  ];

  return (
    <DashboardLayout
      role="admin" roleLabel="Admin Console" nav={nav} active={active} setActive={setActive}
      user={{ name: admin.name || "Admin", sub: "Platform Admin", avatar: ADMIN_AVATAR }}
      logoutTo="/admin/login"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96 text-[#5A6B66]"><Loader2 className="animate-spin mr-2"/> Loading…</div>
      ) : (
        <>
          {active === "overview" && <Overview stats={stats} leads={leads} approvals={approvals} setActive={setActive} />}
          {active === "leads" && <LeadsPanel leads={leads} attorneys={attorneys.filter(a => a.verification_status === "approved")} onRefresh={refresh} />}
          {active === "attorneys" && <AttorneysPanel attorneys={attorneys} />}
          {active === "approvals" && <ApprovalsPanel approvals={approvals} onRefresh={refresh} />}
        </>
      )}
    </DashboardLayout>
  );
}

function Overview({ stats, leads, approvals, setActive }) {
  const s = stats || { leads: 0, attorneys: 0, pending: 0, appointments: 0 };
  const cards = [
    { label: "Total leads", value: s.leads },
    { label: "Active attorneys", value: s.attorneys },
    { label: "Pending approvals", value: s.pending },
    { label: "Scheduled appointments", value: s.appointments },
  ];
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="card p-6">
            <div className="text-sm text-[#5A6B66] mb-2">{c.label}</div>
            <div className="font-display font-extrabold text-4xl text-[#0A2520]">{c.value}</div>
            <div className="text-xs font-semibold mt-2 inline-flex items-center gap-1 text-[#1B6B34]">
              <TrendingUp size={12}/> live
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold text-xl text-[#0A2520]">Pending approvals</h3>
            <button onClick={() => setActive("approvals")} className="text-sm font-semibold text-[#0E6E5E] inline-flex items-center gap-1">Review all <ArrowUpRight size={14}/></button>
          </div>
          <div className="space-y-3">
            {approvals.slice(0, 4).map((a) => (
              <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl surface-sage">
                <img src={a.avatar} alt={a.firm_name} className="w-11 h-11 rounded-xl object-cover"/>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#0A2520] truncate">{a.firm_name || a.name}</div>
                  <div className="text-xs text-[#5A6B66]">{a.city || a.location} · {(a.specializations || []).slice(0,2).join(", ")}</div>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">Pending</span>
              </div>
            ))}
            {approvals.length === 0 && <div className="text-sm text-[#5A6B66] p-3">No pending approvals.</div>}
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold text-xl text-[#0A2520]">Recent leads</h3>
            <button onClick={() => setActive("leads")} className="text-sm font-semibold text-[#0E6E5E] inline-flex items-center gap-1">View all <ArrowUpRight size={14}/></button>
          </div>
          <div className="space-y-3">
            {leads.slice(0, 4).map((l) => (
              <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl surface-sage">
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#0A2520] truncate">{l.patent_title}</div>
                  <div className="text-xs text-[#5A6B66]">{l.customer_name} → {l.attorney_name}</div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${LEAD_STATUS[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</span>
              </div>
            ))}
            {leads.length === 0 && <div className="text-sm text-[#5A6B66] p-3">No leads yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadsPanel({ leads, attorneys, onRefresh }) {
  const [assigning, setAssigning] = useState({}); // {leadId: attorneyId}
  const [saving, setSaving] = useState(null);

  const assign = async (leadId) => {
    const attorneyId = assigning[leadId];
    if (!attorneyId) { toast.error("Pick an attorney first"); return; }
    setSaving(leadId);
    try {
      await api.adminAssignLead(leadId, attorneyId);
      toast.success("Lead assigned to attorney");
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="card p-0 overflow-hidden" data-testid="admin-leads">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="bg-[#0A2520] text-white text-left">
              {["Customer", "Patent brief", "City", "Matched attorney", "Status", "Value", "Assign to attorney"].map((h) => (
                <th key={h} className="p-4 font-display font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l, i) => (
              <tr key={l.id} className={`border-t border-[#F0F4F0] ${i % 2 ? "bg-[#FAFCFA]" : "bg-white"}`} data-testid={`admin-lead-${l.id}`}>
                <td className="p-4">
                  <div className="font-semibold text-[#0A2520]">{l.customer_name}</div>
                  <div className="text-xs text-[#5A6B66]">{l.entity}</div>
                </td>
                <td className="p-4 text-[#0A2520] max-w-[240px]">
                  <div className="truncate font-medium">{l.patent_title}</div>
                  <div className="text-xs text-[#5A6B66]">{l.type}</div>
                </td>
                <td className="p-4 text-[#3D4F4A] whitespace-nowrap">{l.city}</td>
                <td className="p-4 text-[#3D4F4A] whitespace-nowrap">{l.attorney_name || "—"}</td>
                <td className="p-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${LEAD_STATUS[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</span></td>
                <td className="p-4 font-semibold text-[#0A2520] whitespace-nowrap">₹{(l.value || 0).toLocaleString('en-IN')}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <select
                      className="input py-2 text-sm min-w-[200px]"
                      value={assigning[l.id] || l.attorney_id || ""}
                      onChange={(e) => setAssigning((prev) => ({ ...prev, [l.id]: e.target.value }))}
                      data-testid={`assign-select-${l.id}`}
                    >
                      <option value="">— Pick attorney —</option>
                      {attorneys.map((a) => (
                        <option key={a.id} value={a.id}>{a.firm_name || a.name} · {a.city || a.location}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => assign(l.id)}
                      disabled={saving === l.id}
                      className="btn-primary text-xs whitespace-nowrap disabled:opacity-50"
                      data-testid={`assign-btn-${l.id}`}
                    >
                      {saving === l.id ? <Loader2 size={13} className="animate-spin"/> : "Assign"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#5A6B66]">No leads yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AttorneysPanel({ attorneys }) {
  return (
    <div className="card p-0 overflow-hidden" data-testid="admin-attorneys">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="bg-[#0A2520] text-white text-left">
              {["Firm", "Location", "Rating", "Patents", "Tier", "Status"].map((h) => (
                <th key={h} className="p-4 font-display font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {attorneys.map((p, i) => (
              <tr key={p.id} className={`border-t border-[#F0F4F0] ${i % 2 ? "bg-[#FAFCFA]" : "bg-white"}`} data-testid={`admin-attorney-${p.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.avatar && <img src={p.avatar} alt={p.firm_name} className="w-10 h-10 rounded-lg object-cover"/>}
                    <div>
                      <div className="font-semibold text-[#0A2520]">{p.firm_name || p.name || "—"}</div>
                      <div className="text-xs text-[#5A6B66]">{p.tagline || p.name}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-[#3D4F4A] whitespace-nowrap"><MapPin size={12} className="inline -mt-0.5 mr-1"/>{p.location || p.city}</td>
                <td className="p-4 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1 font-semibold text-[#0A2520]">
                    <Star size={12} className="fill-[#7CC242] text-[#7CC242]"/>{p.rating || "—"}
                  </span>
                </td>
                <td className="p-4 text-[#0A2520] font-semibold">{(p.patents_filed || 0).toLocaleString()}</td>
                <td className="p-4">{p.premium ? <span className="chip chip-gold text-xs"><Crown size={10}/> Premium</span> : <span className="text-xs text-[#5A6B66]">Basic</span>}</td>
                <td className="p-4">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap ${
                    p.verification_status === "approved" ? "bg-[#CDEFD4] text-[#1B6B34]" :
                    p.verification_status === "pending" ? "bg-amber-100 text-amber-700" :
                    p.verification_status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-slate-100 text-slate-700"
                  }`}>
                    <BadgeCheck size={11}/> {p.verification_status || "draft"}
                  </span>
                </td>
              </tr>
            ))}
            {attorneys.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#5A6B66]">No attorneys onboarded yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApprovalsPanel({ approvals, onRefresh }) {
  const [busy, setBusy] = useState(null);

  const act = async (id, kind) => {
    setBusy(id + kind);
    try {
      if (kind === "approve") await api.approveAttorney(id);
      else await api.rejectAttorney(id);
      toast.success(kind === "approve" ? "Approved & live" : "Sent back");
      onRefresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-approvals">
      <div className="card p-5 flex items-start gap-3 bg-[#EBF3EC] border-[#7CC242]/30">
        <BadgeCheck size={20} className="text-[#0E6E5E] flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-display font-semibold text-[#0A2520]">{approvals.length} profile{approvals.length !== 1 ? "s" : ""} awaiting your review</div>
          <p className="text-sm text-[#3D4F4A]">Approve to publish changes live. Reject to send back with feedback.</p>
        </div>
      </div>

      {approvals.map((a) => (
        <div key={a.id} className="card p-6" data-testid={`approval-${a.id}`}>
          <div className="grid md:grid-cols-[auto_1fr_auto] gap-5 items-start">
            {a.avatar && <img src={a.avatar} alt={a.firm_name} className="w-16 h-16 rounded-2xl object-cover"/>}
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-display font-bold text-lg text-[#0A2520]">{a.firm_name || a.name || `Unnamed firm · ${a.phone || a.id.slice(-6)}`}</h3>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700">Pending</span>
              </div>
              <div className="text-sm text-[#5A6B66] mb-3">
                {a.name}{a.city && ` · ${a.city || a.location}`}{a.attorneys && ` · ${a.attorneys} attorneys`}{a.submitted_at && ` · submitted ${new Date(a.submitted_at).toLocaleDateString()}`}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {(a.specializations || []).slice(0, 4).map((s) => <span key={s} className="chip text-xs">{s}</span>)}
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-[#CDEFD4] text-[#1B6B34] inline-flex items-center gap-1">
                  <FileText size={11}/> Docs: Ready
                </span>
              </div>
            </div>
            <div className="flex md:flex-col gap-2 md:min-w-[180px] w-full md:w-auto">
              <button
                onClick={() => act(a.id, "approve")}
                disabled={busy === a.id + "approve"}
                className="btn-primary text-sm justify-center disabled:opacity-50"
                data-testid={`approval-approve-${a.id}`}
              >
                {busy === a.id + "approve" ? <Loader2 size={14} className="animate-spin"/> : <><Check size={14}/> Approve</>}
              </button>
              <button
                onClick={() => act(a.id, "reject")}
                disabled={busy === a.id + "reject"}
                className="text-sm font-semibold text-red-600 hover:bg-red-50 rounded-full px-4 py-2.5 inline-flex items-center justify-center gap-1.5 transition disabled:opacity-50"
                data-testid={`approval-reject-${a.id}`}
              >
                {busy === a.id + "reject" ? <Loader2 size={14} className="animate-spin"/> : <><X size={14}/> Reject</>}
              </button>
            </div>
          </div>
        </div>
      ))}
      {approvals.length === 0 && (
        <div className="card p-8 text-center text-[#5A6B66]">All caught up — no pending approvals.</div>
      )}
    </div>
  );
}
